/**
 * Timezone utility functions for converting between UTC and local time
 */

/**
 * Get the user's timezone name (e.g., "EST", "PST", "UTC")
 */
export function getTimezoneName() {
  const date = new Date()
  const timeZoneName = Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short'
  }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value
  
  return timeZoneName || 'UTC'
}

/**
 * Get the user's timezone offset in minutes
 */
export function getTimezoneOffset() {
  return new Date().getTimezoneOffset()
}

/**
 * Convert UTC time string (HH:MM or HH:MM:SS) to local time string (HH:MM)
 * @param {string} utcTime - Time in UTC format (HH:MM or HH:MM:SS)
 * @returns {string} - Time in local timezone format (HH:MM)
 */
export function utcTimeToLocal(utcTime) {
  if (!utcTime) return null
  
  // Parse the UTC time
  const [hours, minutes] = utcTime.split(':').map(Number)
  
  // Create a date object for today at the UTC time
  const today = new Date()
  const utcDate = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
    hours,
    minutes || 0,
    0
  ))
  
  // Convert to local time
  const localHours = String(utcDate.getHours()).padStart(2, '0')
  const localMinutes = String(utcDate.getMinutes()).padStart(2, '0')
  
  return `${localHours}:${localMinutes}`
}

/**
 * Convert local time string (HH:MM) to UTC time string (HH:MM:SS)
 * @param {string} localTime - Time in local timezone format (HH:MM)
 * @returns {string} - Time in UTC format (HH:MM:SS)
 * 
 * Note: When a time range crosses midnight in UTC (e.g., 8 PM EST = 1 AM UTC next day),
 * the end time will be before the start time when stored. This is handled correctly by
 * the email job logic which uses midnight crossover detection.
 */
export function localTimeToUtc(localTime) {
  if (!localTime) return null
  
  // Parse the local time
  const [hours, minutes] = localTime.split(':').map(Number)
  
  // Create a date object for today at the local time
  const today = new Date()
  const localDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    hours,
    minutes || 0,
    0
  )
  
  // Convert to UTC
  // Note: If the local time converts to a time on the next day in UTC,
  // we only store the time portion (HH:MM:SS), losing the "next day" info.
  // This is fine because the email job handles midnight crossover by checking
  // if end < start and using the logic: current >= start OR current <= end
  const utcHours = String(localDate.getUTCHours()).padStart(2, '0')
  const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0')
  const utcSeconds = String(localDate.getUTCSeconds()).padStart(2, '0')
  
  return `${utcHours}:${utcMinutes}:${utcSeconds}`
}

/**
 * Format a timezone offset for display (e.g., "UTC-5", "UTC+2")
 */
export function formatTimezoneOffset() {
  const offset = getTimezoneOffset()
  const hours = Math.abs(Math.floor(offset / 60))
  const minutes = Math.abs(offset % 60)
  const sign = offset <= 0 ? '+' : '-'
  
  if (minutes === 0) {
    return `UTC${sign}${hours}`
  }
  return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * Convert UTC datetime string (ISO format) to local datetime-local format (YYYY-MM-DDTHH:mm)
 * @param {string} utcDateTime - DateTime in UTC ISO format (e.g., "2024-01-15T14:00:00Z")
 * @returns {string} - DateTime in local timezone format (YYYY-MM-DDTHH:mm)
 */
export function utcDateTimeToLocal(utcDateTime) {
  if (!utcDateTime) return ''
  
  // Parse the UTC datetime
  const utcDate = new Date(utcDateTime)
  
  // Get local date components
  const year = utcDate.getFullYear()
  const month = String(utcDate.getMonth() + 1).padStart(2, '0')
  const day = String(utcDate.getDate()).padStart(2, '0')
  const hours = String(utcDate.getHours()).padStart(2, '0')
  const minutes = String(utcDate.getMinutes()).padStart(2, '0')
  
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

